import { useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Icon } from '../components/Icon'
import { languageFromPath, withLang } from '../i18n/i18n'
import { useDocumentMeta } from '../i18n/useDocumentMeta'

// Compatible Devices catalogue page. Display chrome (hero, filter labels,
// CTA, notes) lives in src/i18n/locales/{lang}/compat.json. Brand display
// names and per-brand taglines are also translated through that namespace.
// Model names (iPhone 13, Galaxy S22, etc.) are proper nouns — kept in
// English in the data below.

type Category = 'ios' | 'ipad' | 'android' | 'laptop'

interface GroupNote {
  type: 'warn' | 'info'
  key: string // e.g. "note1", looked up under groups.<key>.note1 in compat.json
}

interface DeviceGroup {
  key: string // matches groups.<key> in compat.json
  cat: Category
  icon: React.ReactNode | null
  models: string[]
  noteKeys: GroupNote[]
}

const GROUPS: DeviceGroup[] = [
  {
    key: 'apple-iphone',
    cat: 'ios',
    icon: <Icon name="apple" size={18} />,
    models: [
      'iPhone XS', 'iPhone XS Max', 'iPhone XR',
      'iPhone 11', 'iPhone 11 Pro', 'iPhone 11 Pro Max',
      'iPhone SE (2020)',
      'iPhone 12', 'iPhone 12 mini', 'iPhone 12 Pro', 'iPhone 12 Pro Max',
      'iPhone 13', 'iPhone 13 mini', 'iPhone 13 Pro', 'iPhone 13 Pro Max',
      'iPhone SE (2022)',
      'iPhone 14', 'iPhone 14 Plus', 'iPhone 14 Pro', 'iPhone 14 Pro Max',
      'iPhone 15', 'iPhone 15 Plus', 'iPhone 15 Pro', 'iPhone 15 Pro Max',
      'iPhone 16', 'iPhone 16 Plus', 'iPhone 16 Pro', 'iPhone 16 Pro Max',
    ],
    noteKeys: [{ type: 'warn', key: 'note1' }],
  },
  {
    key: 'apple-ipad',
    cat: 'ipad',
    icon: <Icon name="apple" size={18} />,
    models: [
      'iPad Pro 11" (A2068, 2020+)',
      'iPad Pro 12.9" (A2069, 2020+)',
      'iPad Air (A2123, 2019+)',
      'iPad (A2198, 2019+)',
      'iPad Mini (A2124, 2019+)',
    ],
    noteKeys: [
      { type: 'warn', key: 'note1' },
      { type: 'warn', key: 'note2' },
    ],
  },
  {
    key: 'samsung',
    cat: 'android',
    icon: <Icon name="android" size={18} />,
    models: [
      'Galaxy S20', 'Galaxy S20+', 'Galaxy S20 Ultra',
      'Galaxy S21', 'Galaxy S21+ 5G', 'Galaxy S21+ Ultra 5G',
      'Galaxy S22', 'Galaxy S22+', 'Galaxy S22 Ultra',
      'Galaxy S23', 'Galaxy S23+', 'Galaxy S23 Ultra', 'Galaxy S23 FE',
      'Galaxy S24', 'Galaxy S24+', 'Galaxy S24 Ultra',
      'Galaxy Note 20', 'Galaxy Note 20 Ultra 5G',
      'Galaxy Fold', 'Galaxy Z Fold2 5G', 'Galaxy Z Fold3 5G', 'Galaxy Z Fold4', 'Galaxy Z Fold 5', 'Galaxy Z Fold 6',
      'Galaxy Z Flip', 'Galaxy Z Flip3 5G', 'Galaxy Z Flip 4', 'Galaxy Z Flip 5', 'Galaxy Z Flip 6',
    ],
    noteKeys: [
      { type: 'warn', key: 'note1' },
      { type: 'warn', key: 'note2' },
      { type: 'warn', key: 'note3' },
    ],
  },
  {
    key: 'google',
    cat: 'android',
    icon: <Icon name="google" size={18} />,
    models: [
      'Pixel 2 (Google Fi only)',
      'Pixel 3', 'Pixel 3 XL', 'Pixel 3a', 'Pixel 3a XL',
      'Pixel 4', 'Pixel 4 XL', 'Pixel 4a', 'Pixel 4a 5G',
      'Pixel 5', 'Pixel 5a',
      'Pixel 6', 'Pixel 6 Pro', 'Pixel 6a',
      'Pixel 7', 'Pixel 7 Pro', 'Pixel 7a',
      'Pixel 8', 'Pixel 8 Pro', 'Pixel 8a',
      'Pixel 9', 'Pixel 9 Pro', 'Pixel 9 Pro XL',
      'Pixel Fold',
    ],
    noteKeys: [
      { type: 'warn', key: 'note1' },
      { type: 'warn', key: 'note2' },
    ],
  },
  {
    key: 'huawei',
    cat: 'android',
    icon: null,
    models: ['Huawei P40', 'Huawei P40 Pro', 'Huawei Mate 40 Pro'],
    noteKeys: [{ type: 'warn', key: 'note1' }],
  },
  {
    key: 'oppo',
    cat: 'android',
    icon: null,
    models: ['Oppo Find X3 Pro', 'Oppo Reno 5A', 'Oppo Find X5', 'Oppo Find X5 Pro'],
    noteKeys: [{ type: 'warn', key: 'note1' }],
  },
  {
    key: 'sony',
    cat: 'android',
    icon: null,
    models: ['Sony Xperia 10 III Lite', 'Sony Xperia 10 IV', 'Sony Xperia 1 IV', 'Sony Xperia 5 IV'],
    noteKeys: [],
  },
  {
    key: 'motorola',
    cat: 'android',
    icon: null,
    models: ['Motorola Razr 2019', 'Motorola Razr 5G', 'Motorola Razr 40 Ultra'],
    noteKeys: [],
  },
  {
    key: 'other-phones',
    cat: 'android',
    icon: null,
    models: [
      'Gemini PDA',
      'Rakuten Mini', 'Rakuten Big-S', 'Rakuten Big', 'Rakuten Hand', 'Rakuten Hand 5G',
      'Surface Pro X (phone)',
      'Honor Magic 4 Pro', 'Honor Magic 5', 'Honor Magic 6 Pro',
      'Fairphone 4',
      'Sharp Aquos Sense6s', 'Sharp Aquos Wish',
      'Xiaomi 12T Pro', 'Xiaomi 13', 'Xiaomi 13 Pro', 'Xiaomi 14 Ultra',
      'DOOGEE V30',
      'OnePlus 11', 'OnePlus 12', 'OnePlus Open',
      'Nothing Phone (1)', 'Nothing Phone (2)', 'Nothing Phone (2a)',
    ],
    noteKeys: [],
  },
  {
    key: 'laptops',
    cat: 'laptop',
    icon: null,
    models: [
      'Microsoft Surface Pro X',
      'Microsoft Surface Go 2',
      'Microsoft Surface Pro LTE Advanced',
      'Acer Swift 3', 'Acer Swift 7',
      'Asus Mini Transformer', 'Asus NovaGo', 'Asus VivoBook Flip 14',
      'HP EliteBook G5', 'HP ProBook G5', 'HP ZBook G5', 'HP Spectre Folio 13',
      'Lenovo Yoga C630', 'Lenovo Miix 630', 'Lenovo Yoga 520',
      'Samsung Galaxy Book 2',
    ],
    noteKeys: [],
  },
]

type Filter = 'all' | Category

const CATS: Filter[] = ['all', 'ios', 'ipad', 'android', 'laptop']

export default function CompatibleDevices() {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation('compat')
  const { t: tCommon } = useTranslation('common')
  const lang = languageFromPath(location.pathname)
  useDocumentMeta({ title: t('seo.title'), description: t('seo.description') })
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<Filter>('all')

  const ql = query.toLowerCase().trim()

  const visibleGroups = useMemo(() => {
    return GROUPS
      .filter((g) => filter === 'all' || g.cat === filter)
      .map((g) => ({
        ...g,
        models: ql ? g.models.filter((m) => m.toLowerCase().includes(ql)) : g.models,
      }))
      .filter((g) => g.models.length > 0 || (!ql && filter !== 'all'))
  }, [filter, ql])

  const totalSupported = useMemo(
    () => GROUPS.reduce((sum, g) => sum + g.models.length, 0),
    [],
  )

  const catCount = (id: Filter) =>
    id === 'all'
      ? totalSupported
      : GROUPS.filter((g) => g.cat === id).reduce((s, g) => s + g.models.length, 0)

  return (
    <>
      <section className="compat-page-hero">
        <div className="section-inner">
          <div className="breadcrumb" style={{ marginBottom: 18 }}>
            <Link to={withLang('/how-it-works', lang)} style={{ color: 'var(--ink-3)' }}>
              {tCommon('nav.howItWorks')}
            </Link>
            <span> / </span>
            <span style={{ color: 'var(--ink)' }}>{t('breadcrumb')}</span>
          </div>
          <h1 className="compat-page-h1">
            {t('hero.titlePart1')}<em>{t('hero.titleEmphasis')}</em>
          </h1>
          <p className="lede compat-page-lede">
            {t('hero.lede', { count: totalSupported })}
          </p>

          <div className="compat-page-search">
            <Icon name="search" size={18} />
            <input
              type="search"
              placeholder={t('hero.searchPlaceholder')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label={t('hero.searchAriaLabel')}
            />
            {query && (
              <button
                type="button"
                className="compat-page-search-clear"
                onClick={() => setQuery('')}
                aria-label="Clear search"
              >
                <Icon name="x" size={12} />
              </button>
            )}
          </div>

          <div className="compat-page-filter-tabs">
            {CATS.map((id) => (
              <button
                key={id}
                type="button"
                className={`compat-page-filter-tab ${filter === id ? 'active' : ''}`}
                onClick={() => setFilter(id)}
              >
                <span>{t(`filters.${id}`)}</span>
                <span className="count">{catCount(id)}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="section compat-page-body">
        <div className="section-inner">
          {visibleGroups.map((g) => (
            <div key={g.key} className="compat-brand">
              <div className="compat-brand-h">
                <div>
                  <h2>
                    {g.icon && <span className="compat-brand-icon">{g.icon}</span>}
                    {t(`groups.${g.key}.brand`)}
                  </h2>
                  <div className="compat-brand-tagline">{t(`groups.${g.key}.tagline`)}</div>
                </div>
                <span className="compat-brand-count mono">{t('modelsLabel', { count: g.models.length })}</span>
              </div>

              <div className="compat-model-grid">
                {g.models.map((m) => (
                  <div key={m} className="compat-model-pill">
                    <Icon name="check" size={12} />
                    <span>{m}</span>
                  </div>
                ))}
              </div>

              {g.noteKeys.length > 0 && (
                <div className="compat-notes">
                  {g.noteKeys.map((n, i) => (
                    <div key={i} className={`compat-note ${n.type}`}>
                      <span className="compat-note-icon">{n.type === 'warn' ? '!' : 'i'}</span>
                      <div>{t(`groups.${g.key}.${n.key}`)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          {ql && visibleGroups.length === 0 && (
            <div className="compat-empty">
              {t('emptyState', { query })}
            </div>
          )}

          <div className="compat-bottom-cta">
            <h3>{t('bottomCta.title')}</h3>
            <p className="muted compat-bottom-cta-body">{t('bottomCta.body')}</p>
            <div className="compat-bottom-cta-row">
              <button type="button" className="btn primary" onClick={() => navigate(withLang('/destinations', lang))}>
                {t('bottomCta.browse')}
              </button>
              <Link to={withLang('/how-it-works', lang)} className="btn ghost">{t('bottomCta.back')}</Link>
              <a href="mailto:support@nimvoy.com" className="btn ghost">{t('bottomCta.support')}</a>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
